import { useState } from 'react';
import {
	Box,
	Button,
	VStack,
	Text,
	HStack,
	Flex,
	Icon,
	Input,
	FormControl,
	FormLabel,
	useToast,
} from '@chakra-ui/react';
import { FaExclamationTriangle, FaArrowRight, FaUsers, FaWrench, FaDollarSign } from 'react-icons/fa';

import type { CreateStep } from './SetupStepper';
import { StepFooter } from './StepFooter';

interface ITSetupStepProps {
	setCurrentStep: (step: CreateStep) => void;
	organizationId: string;
	nextStepAfterItSetup?: CreateStep;
}

const PARTBAY_BLUE = '#0071E3';

type ITOption = 'invite' | 'self' | 'partbay' | '';

export const ITSetupStep = ({
	setCurrentStep,
	organizationId,
	nextStepAfterItSetup = 'supplier-network',
}: ITSetupStepProps) => {
	const [showOptions, setShowOptions] = useState(false);
	const [selectedOption, setSelectedOption] = useState<ITOption>('');
	const [inviteEmail, setInviteEmail] = useState('');
	const [isSendingInvite, setIsSendingInvite] = useState(false);
	const toast = useToast();

	const ensureOrgSettings = async () => {
		try {
			const API_URL = (await import('../../constants/environment')).default;
			await (await import('axios')).default.post(
				`${API_URL}/onboarding/create-organization-settings`,
				{ organizationId, preset: 'custom' }
			);
		} catch {
			// Non-critical — orgSettings may already exist or admin will create later
		}
	};

	const handleContinue = async () => {
		await ensureOrgSettings();

		if (selectedOption === 'invite') {
			if (!inviteEmail.trim()) {
				toast({
					title: 'Email required',
					description: 'Please enter an email address to send the IT invite.',
					status: 'warning',
					duration: 3000,
					isClosable: true,
				});
				return;
			}
			setIsSendingInvite(true);
			try {
				const API_URL = (await import('../../constants/environment')).default;
				const sessionId = localStorage.getItem('session_id');
				await (await import('axios')).default.post(
					`${API_URL}/settings/account/it-setup`,
					{ action: 'invite-it', email: inviteEmail.trim(), organizationId },
					{ headers: { Authorization: sessionId ?? '' } }
				);
				toast({
					title: 'Invite sent',
					description: `An IT setup invite has been sent to ${inviteEmail.trim()}.`,
					status: 'success',
					duration: 5000,
					isClosable: true,
				});
			setCurrentStep(nextStepAfterItSetup);
		} catch {
			toast({
				title: 'Failed to send invite',
				description: 'Could not send the IT invite. Please try again.',
				status: 'error',
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setIsSendingInvite(false);
		}
	} else if (selectedOption === 'self') {
		setCurrentStep(nextStepAfterItSetup);
	} else if (selectedOption === 'partbay') {
		window.open('https://cal.com/aiprocurement/it-setup', '_blank');
		setCurrentStep(nextStepAfterItSetup);
	}
	};

	if (!showOptions) {
		return (
			<Box position="relative" width="100%" minH={0} h="calc(100vh - 200px)" display="flex" flexDirection="column">
				<Box flex={1} display="flex" alignItems="center" justifyContent="center">
					<Box
						borderRadius="xl"
						overflow="hidden"
						shadow="none"
						maxW="520px"
						mx="auto"
						bg="white"
					>
						<Box bg={PARTBAY_BLUE} py={6} display="flex" flexDirection="column" alignItems="center">
							<Box position="relative" w="56px" h="56px" mb={3}>
								<Box
									position="absolute"
									inset={0}
									borderRadius="full"
									bg="whiteAlpha.300"
									sx={{
										animation: 'cautionPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
										'@keyframes cautionPulse': {
											'0%, 100%': { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.3)' },
											'50%': { transform: 'scale(1.12)', boxShadow: '0 0 0 12px rgba(255, 255, 255, 0)' },
										},
									}}
								/>
								<Flex position="relative" w="56px" h="56px" borderRadius="full" bg="whiteAlpha.200" align="center" justify="center">
									<Icon as={FaExclamationTriangle as React.ElementType} color="white" boxSize={6} />
								</Flex>
							</Box>
							<Text fontSize="lg" fontWeight="bold" color="white">IT Setup Required</Text>
						</Box>
						<Box pt={6} pb={4} px={8}>
							<Text fontSize="sm" color="gray.600" textAlign="center" mb={4}>
								The Yaxle desktop application requires installation and configuration on your server(s).
								This step connects your estimating systems (CCC ONE, Mitchell) to the parts marketplaces
								through Yaxle. Without it, EMS files cannot be routed and parts ordering will not function.
							</Text>
							<Text fontSize="sm" color="gray.500" textAlign="center" lineHeight="tall">
								On the next screen, you'll choose how you'd like to handle IT setup — whether through
								your own IT team, yourself, or by scheduling a session with Yaxle.
							</Text>
						</Box>
						<Flex pb={6} px={8} justifyContent="center">
							<Button
								bg={PARTBAY_BLUE}
								color="white"
								_hover={{ bg: '#3141a5' }}
								size="sm"
								borderRadius="md"
								px={8}
								onClick={() => setShowOptions(true)}
							>
								Continue
							</Button>
						</Flex>
					</Box>
				</Box>
			</Box>
		);
	}

	return (
		<Box position="relative" width="100%" minH={0} h="calc(100vh - 200px)" display="flex" flexDirection="column">
			<Box flex={1} overflowY="auto" px={{ base: 2, md: 8 }} py={4} display="flex" alignItems="center" justifyContent="center">
				<Box maxW="640px" mx="auto" w="100%">
					<VStack spacing={6} align="stretch">
						<VStack spacing={2} align="center">
							<Text fontSize="xl" fontWeight="bold" color="gray.800">
								How would you like to handle IT setup?
							</Text>
							<Text fontSize="sm" color="gray.500" textAlign="center">
								Choose the option that best fits your organization.
							</Text>
						</VStack>

						<VStack spacing={3} align="stretch">
							{/* Option 1: Invite IT */}
							<Box
								as="button"
								type="button"
								onClick={() => setSelectedOption('invite')}
								borderWidth="2px"
								borderColor={selectedOption === 'invite' ? PARTBAY_BLUE : 'gray.200'}
								bg={selectedOption === 'invite' ? `${PARTBAY_BLUE}08` : 'white'}
								borderRadius="xl"
								p={5}
								transition="all 0.2s ease"
								_hover={{ borderColor: selectedOption === 'invite' ? PARTBAY_BLUE : 'gray.300', shadow: 'none' }}
								textAlign="left"
								shadow="none"
							>
								<HStack spacing={4} align="center" justify="space-between">
									<HStack spacing={4} align="center">
										<Flex w="42px" h="42px" borderRadius="xl" bg={selectedOption === 'invite' ? PARTBAY_BLUE : `${PARTBAY_BLUE}12`} align="center" justify="center" flexShrink={0}>
											<Icon as={FaUsers as React.ElementType} color={selectedOption === 'invite' ? 'white' : PARTBAY_BLUE} boxSize={4} />
										</Flex>
										<Box>
											<Text fontSize="md" fontWeight="bold" color={selectedOption === 'invite' ? PARTBAY_BLUE : 'gray.800'}>I have an IT person / team</Text>
											<Text fontSize="sm" color="gray.500" mt={0.5}>
												For shops with a dedicated IT team or person experienced with setting up software.
											</Text>
										</Box>
									</HStack>
									{selectedOption === 'invite' && <Icon as={FaArrowRight as React.ElementType} color={PARTBAY_BLUE} boxSize={3.5} />}
								</HStack>
							</Box>

							{/* Option 2: Self-setup */}
							<Box
								as="button"
								type="button"
								onClick={() => setSelectedOption('self')}
								borderWidth="2px"
								borderColor={selectedOption === 'self' ? PARTBAY_BLUE : 'gray.200'}
								bg={selectedOption === 'self' ? `${PARTBAY_BLUE}08` : 'white'}
								borderRadius="xl"
								p={5}
								transition="all 0.2s ease"
								_hover={{ borderColor: selectedOption === 'self' ? PARTBAY_BLUE : 'gray.300', shadow: 'none' }}
								textAlign="left"
								shadow="none"
							>
								<HStack spacing={4} align="center" justify="space-between">
									<HStack spacing={4} align="center">
										<Flex w="42px" h="42px" borderRadius="xl" bg={selectedOption === 'self' ? PARTBAY_BLUE : `${PARTBAY_BLUE}12`} align="center" justify="center" flexShrink={0}>
											<Icon as={FaWrench as React.ElementType} color={selectedOption === 'self' ? 'white' : PARTBAY_BLUE} boxSize={4} />
										</Flex>
										<Box>
											<Text fontSize="md" fontWeight="bold" color={selectedOption === 'self' ? PARTBAY_BLUE : 'gray.800'}>I will set it up myself</Text>
											<Text fontSize="sm" color="gray.500" mt={0.5}>
												For shop owners / employees who have set up CCC ONE, Mitchell, and marketplace software before.
											</Text>
										</Box>
									</HStack>
									{selectedOption === 'self' && <Icon as={FaArrowRight as React.ElementType} color={PARTBAY_BLUE} boxSize={3.5} />}
								</HStack>
							</Box>

							{/* Option 3: Pay Yaxle */}
							<Box
								as="button"
								type="button"
								onClick={() => setSelectedOption('partbay')}
								borderWidth="2px"
								borderColor={selectedOption === 'partbay' ? PARTBAY_BLUE : 'gray.200'}
								bg={selectedOption === 'partbay' ? `${PARTBAY_BLUE}08` : 'white'}
								borderRadius="xl"
								p={5}
								transition="all 0.2s ease"
								_hover={{ borderColor: selectedOption === 'partbay' ? PARTBAY_BLUE : 'gray.300', shadow: 'none' }}
								textAlign="left"
								shadow="none"
							>
								<HStack spacing={4} align="center" justify="space-between">
									<HStack spacing={4} align="center">
										<Flex w="42px" h="42px" borderRadius="xl" bg={selectedOption === 'partbay' ? PARTBAY_BLUE : `${PARTBAY_BLUE}12`} align="center" justify="center" flexShrink={0}>
											<Icon as={FaDollarSign as React.ElementType} color={selectedOption === 'partbay' ? 'white' : PARTBAY_BLUE} boxSize={4} />
										</Flex>
										<Box>
											<Text fontSize="md" fontWeight="bold" color={selectedOption === 'partbay' ? PARTBAY_BLUE : 'gray.800'}>Have Yaxle do it — $100 / store</Text>
											<Text fontSize="sm" color="gray.500" mt={0.5}>
												Don't have an IT team or feel comfortable setting it up yourself? We'll handle everything for a one-time fee.
											</Text>
										</Box>
									</HStack>
									{selectedOption === 'partbay' && <Icon as={FaArrowRight as React.ElementType} color={PARTBAY_BLUE} boxSize={3.5} />}
								</HStack>
							</Box>
						</VStack>

						{selectedOption === 'invite' && (
							<Box borderWidth={1} borderRadius="lg" p={4} bg="gray.50">
								<FormControl>
									<FormLabel fontSize="sm">IT contact email address</FormLabel>
									<Input
										size="sm"
										type="email"
										value={inviteEmail}
										onChange={(e) => setInviteEmail(e.target.value)}
										placeholder="it@yourcompany.com"
									/>
									<Text fontSize="xs" color="gray.500" mt={2}>
										We'll send them a link to create an IT account and guide them through setup.
									</Text>
								</FormControl>
							</Box>
						)}
					</VStack>
				</Box>
			</Box>

			<StepFooter
				back={{ onClick: () => setShowOptions(false) }}
				primary={{
					label: selectedOption === 'invite'
						? 'Send Invite & Continue'
						: selectedOption === 'partbay'
							? 'Schedule & Continue'
							: 'Continue',
					onClick: handleContinue,
					disabled: selectedOption === '',
					isLoading: isSendingInvite,
					loadingText: 'Sending invite...',
				}}
			/>
		</Box>
	);
};
